/* LigandMod.jsx  -*- mode: javascript; indent-tab-mode:nil; fill-column: 90; -*-
 *
 * Display a list of suggested modifications and replace them.
 *
 * Important ideas:
 * - FragmentSearchResult is a model class that contains data from bfd-server/dataservice,
 *   tying together 3D fragment poses and metadata about fragment and search.
 * - AnnotatedSearchResult wraps the FragmentSearchResult with other information such as
 *   fragment properties and protein target info.
 * - Unfortunately, the word "suggestion" is used for both of these classes, but usually
 *   it refers to the annotated version.
 * - These search results will get rendered in tabular form, either csv or in a GUI table.
 * - column_definitions.js is a central place to define available columns and how they
 *   are rendered in csv or GUI. LigandMod.getColumns() decides which columns to show.
 */
import React from 'react';
import debounce from 'lodash/debounce';
import { App } from 'BMapsSrc/BMapsApp';
import {
    redisplay, makeTempMolecule, removeTempMolecule, updateTempMolecule,
} from 'BMapsSrc/MainCanvas';
import { EventBroker } from 'BMapsSrc/eventbroker';
import { showAlert, hasPreviewModeError } from 'BMapsSrc/utils';
import { updateTooltipster } from 'BMapsUI/ui_utils';
import { MolStyles } from 'BMapsSrc/style_manager';
import { FragList } from 'BMapsSrc/FragList';
import { UserActions } from 'BMapsCmds';
import LinkLikeButton from 'BMapsUI/common/LinkLikeButton';
import { SelectivityColoring } from 'BMapsPresentation/SelectivityColoring';
import { HydrogenDisplayOptions, hydrogenCheck } from 'BMapsUtil/display_utils';
import { EnergySlider } from 'BMapsUI/UIComponents';
import { AnnotatedSearchResult, ActivityThresholdInfo } from './AnnotatedSearchResult';
import { GlobalFilterSlider } from './GlobalFilterSlider';
import { buildGlobalScoreFilter } from './primary_score_filter';
import { SuggestionTable } from './SuggestionTable';
import { getCsvData } from './FragmentSearchCsv';
import {
    renderDebug, sortSearchResults, LigandModUIInfo, isKinaseInProjectCases,
} from './utils';
import {
    FragmentSearchResultColumns, makePerProteinCol,
    ActiveTargetColor, ActiveOffTargetColor, defaultAscendingForColumn,
} from './column_definitions';

const defaultPrimaryScoreField = 'enSolute';

/** Trailing-edge debounce window for the global primary-score filter
 *  slider. Drag events update `filterThresholds` (and the slider thumb)
 *  immediately, but the actual filter rebuild + per-suggestion recalc
 *  is coalesced into one trailing call after the pointer pauses for
 *  this many ms. Chosen to be just past typical browser input-event
 *  cadence (~16-50ms) so a continuous drag never fires interim
 *  recalcs, while feeling instant on release. */
const FILTER_COMMIT_DEBOUNCE_MS = 150;

/** Debounce window for `updateTooltipster` from `componentDidUpdate`.
 *  Tooltip bindings only need refreshing when row DOM structure changes
 *  (rows added/removed/sorted), never per state update — and a slider
 *  drag fires `setState` per event tick, each of which would otherwise
 *  trigger a jQuery `.each()` over thousands of `.suggestionRow button`
 *  nodes. 200 ms is below the threshold at which users would notice
 *  tooltips "lagging" their cursor. */
const TOOLTIP_REFRESH_DEBOUNCE_MS = 200;

/**
 * Single-slot memoizer: returns a wrapped function that caches the last
 * call's arguments and result, returning the cached result if the new
 * arguments are strictly equal (`===`) to the cached ones, otherwise
 * recomputing.
 *
 * Used inside `LigandMod` to give downstream `PureComponent` children
 * stable prop references across renders. Without this, derived values
 * like `projectCases` and `activityInfo` would be fresh objects every
 * tick, defeating `SuggestionRow`'s shallow prop-compare and forcing
 * ~3000 rows to re-render on every slider drag tick.
 *
 * Not `lodash/memoize` because that one keys only on the first argument
 * by default; we want strict-equality over all args.
 *
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @returns {F}
 */
function memoizeLast(fn) {
    let lastArgs;
    let lastResult;
    return ((...args) => {
        if (lastArgs
            && lastArgs.length === args.length
            && args.every((a, i) => a === lastArgs[i])) {
            return lastResult;
        }
        lastArgs = args;
        lastResult = fn(...args);
        return lastResult;
    });
}

/**
 * Panel component for browsing and accepting fragment search results.
 * See file-level comment above for architecture notes.
 */
export default class LigandMod extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            /** Whether the panel is rendered */
            visible: false,
            /** @type {AnnotatedSearchResult[]} Current batch of search results */
            suggestions: [],
            /** @type {Map<AnnotatedSearchResult, { pinned: boolean }>} Per-suggestion UI state */
            suggestionState: new Map(),
            /** @type {Atom} The atom the user searched from */
            sourceAtom: null,
            /** Column key to sort by, or 'default' to use primaryScoreField */
            sortCol: 'default',
            sortAscending: defaultAscendingForColumn(defaultPrimaryScoreField),
            /** Show fragment 2D structure images instead of names in the first column */
            showImages: true,
            /** @type {LigandModUIInfo} Labels and feature presence for the current search type */
            uiInfo: new LigandModUIInfo('grow'),
            /** Whether the options panel is expanded */
            showOptions: false,
            /** Keep the panel open and accumulate fragments instead of closing after first accept
             */
            addMultipleFrags: false,
            /** Show all hydrogens on fragment previews (not just polar) */
            displayFragmentHydrogens: false,
            /** Whether activity threshold row highlighting is enabled */
            highlightActiveFrags: false,
            /** @type {ActivityThresholdInfo} Threshold config for activity row highlighting */
            activityThresholdInfo: new ActivityThresholdInfo({
                bestPoseOrderField: defaultPrimaryScoreField,
                activityField: defaultPrimaryScoreField,
            }),
            /** @type {{ [field: string]: number }} Last-used threshold per scoring field;
             *  absent entries use the field's built-in default */
            activityThresholds: {},
            /** @type {{ [field: string]: number }} Last-used primary-score filter
             *  threshold per scoring field. Absent entries mean the slider is at its
             *  permissive end (no filter installed). Mirrors `activityThresholds`. */
            filterThresholds: {},
            /** @type {PosePropertyFilter | null} Cached global primary-score filter
             *  derived from `filterThresholds[primaryScoreField]`. Updated in lockstep
             *  by `handleFilterThreshold` and `handlePrimaryScoreField` (and rebuilt
             *  on a fresh result set by `displaySuggestions`). */
            globalFilter: null,
            /** @type {Set<string>} Column keys opted into beyond the defaults */
            includedColumns: new Set(),
            /** How to order poses in the pose explorer slider
             *  ('position'|'bindingScore'|'enSolute'|...)
             *  Applies to all suggestion rows */
            sortPosesBy: 'position',
            /** @type {'enSolute'|'bindingScore'} Active scoring field: drives table sort order,
             *  best-pose selection, score ratio, and activity highlight threshold */
            primaryScoreField: defaultPrimaryScoreField,
            /** @type {{ [suggName: string]: { [propId: string]: PosePropertyFilter } }}
             *  Active pose filters per suggestion row */
            allDisplayFilters: {},
            /** When true, mousing out of a pinned row collapses display to the selected pose only;
             *  mousing back in or using the slider restores the full cloud */
            pinOnlyHighlightedPose: false,
        };
        this.handleDisplaySuggestions = this.handleDisplaySuggestions.bind(this);
        this.hide = this.hide.bind(this);
        this.suggestionAccept = this.suggestionAccept.bind(this);
        this.suggestionEnter = this.suggestionEnter.bind(this);
        this.suggestionExit = this.suggestionExit.bind(this);
        this.cycleSuggestions = this.cycleSuggestions.bind(this);
        this.togglePin = this.togglePin.bind(this);
        this.handleMultipleFrags = this.handleMultipleFrags.bind(this);
        this.handleFragmentHydrogens = this.handleFragmentHydrogens.bind(this);
        this.scrollingRef = React.createRef(); // Used for reseting scroll after sort change
        this.getCsvString = this.getCsvString.bind(this);
        this.handleCsvDownload = this.handleCsvDownload.bind(this);
        this.toggleImages = this.toggleImages.bind(this);
        this.handleSort = this.handleSort.bind(this);
        this.getSortedSuggestions = this.getSortedSuggestions.bind(this);
        this.isPinned = this.isPinned.bind(this);
        this.handleHighlightActiveFrags = this.handleHighlightActiveFrags.bind(this);
        this.handleActivityThreshold = this.handleActivityThreshold.bind(this);
        this.handleFilterThreshold = this.handleFilterThreshold.bind(this);
        this.commitFilterThreshold = this.commitFilterThreshold.bind(this);
        /** Debounced wrapper around `commitFilterThreshold` invoked from
         *  `handleFilterThreshold`. `.cancel()` is called from
         *  `componentWillUnmount`; `.flush()` is available if a sibling
         *  handler needs to force the pending commit synchronously. */
        this.commitFilterThresholdDebounced = debounce(
            this.commitFilterThreshold, FILTER_COMMIT_DEBOUNCE_MS
        );
        this.updateTooltips = this.updateTooltips.bind(this);
        /** Debounced tooltip refresh. Cancelled on unmount. */
        this.updateTooltipsDebounced = debounce(
            this.updateTooltips, TOOLTIP_REFRESH_DEBOUNCE_MS
        );
        // Single-slot memoizers for derived render values whose stability
        // matters: `SuggestionTable` and `SuggestionRow` are PureComponents,
        // and without stable references on the props they receive, every
        // drag tick re-renders all rows.
        //
        // `memoSortedSuggestions`, `memoColumns`, and `memoProjectCases`
        // are kept separate so a sort-column click — which invalidates
        // ONLY the sorted array — leaves the columns and projectCases
        // references untouched, letting `SuggestionRow`'s PureComponent
        // shallow-compare pass for unchanged rows. (Columns and project
        // cases derive from the SET of suggestions, not their order.)
        this.memoSortedSuggestions = memoizeLast(
            (suggestions, sortCol, sortAscending, primaryScoreField) => {
                const sorted = [...suggestions];
                sorted.sort(sortSearchResults({
                    sortCol, sortAscending, defaultSortField: primaryScoreField,
                }));
                return sorted;
            }
        );
        this.memoColumns = memoizeLast(
            (suggestions, uiInfo, showImages, includedColumns, primaryScoreField) => (
                this.getColumns(
                    suggestions, uiInfo, showImages, includedColumns, primaryScoreField
                )
            )
        );
        this.memoProjectCases = memoizeLast(
            // Keyed on the unsorted suggestions array — project-case set
            // is invariant under row reordering, so a sort click must NOT
            // invalidate this.
            (suggestions) => AnnotatedSearchResult.getProjectCasesForSuggestions(
                suggestions
            )
        );
        this.memoVisibleSuggestions = memoizeLast(
            // `globalFilter` participates in the cache key only as a
            // change-signal — when it changes, `sugg.globalFilteredCount`
            // may have changed and the filter must rerun. The closure
            // body itself only reads `globalFilteredCount`.
            // eslint-disable-next-line no-unused-vars
            (sortedSuggestions, globalFilter) => sortedSuggestions.filter(
                (s) => s.globalFilteredCount > 0
            )
        );
        this.memoActivityInfo = memoizeLast(
            (highlightActiveFrags, activityThresholdInfo) => (
                { highlightActiveFrags, activityThresholdInfo }
            )
        );
        this.handleShowOptions = this.handleShowOptions.bind(this);
        this.toggleColumns = this.toggleColumns.bind(this);
        this.handleSortPosesBy = this.handleSortPosesBy.bind(this);
        this.handlePrimaryScoreField = this.handlePrimaryScoreField.bind(this);
        this.handleUpdateDisplayFilters = this.handleUpdateDisplayFilters.bind(this);
        this.handlePinOnlyHighlightedPose = this.handlePinOnlyHighlightedPose.bind(this);
    }

    componentDidMount() {
        EventBroker.subscribe('displaySuggestions', this.handleDisplaySuggestions);
        EventBroker.subscribe('escapeKey', this.hide);
        EventBroker.subscribe('zapAll', this.hide);
        this.updateTooltips();
    }

    componentDidUpdate(prevProps, prevState) {
        const { sortCol, sortAscending } = this.state;
        if (prevState.sortCol !== sortCol
            || prevState.sortAscending !== sortAscending) {
            if (this.scrollingRef.current) {
                this.scrollingRef.current.scrollTop = 0;
            }
        }
        this.updateTooltipsDebounced();
    }

    componentWillUnmount() {
        EventBroker.unsubscribe('displaySuggestions', this.handleDisplaySuggestions);
        EventBroker.unsubscribe('escapeKey', this.hide);
        EventBroker.unsubscribe('zapAll', this.hide);
        this.commitFilterThresholdDebounced.cancel();
        this.updateTooltipsDebounced.cancel();
    }

    handleDisplaySuggestions(evtName, {
        suggestions: suggestionsIn, errors, sourceAtom, cmd, fragList,
    }) {
        function isSupportedMod(modType) {
            return modType === 'Near'
                || !!FragmentSearchResultColumns.linkColumns.modType.table.getValue(modType);
        }
        // Filter out linkers that the server sends but aren't yet recognized by the client.
        const suggestions = suggestionsIn.filter((sugg) => isSupportedMod(sugg.modType));
        if (suggestions.length !== suggestionsIn.length) {
            const filtered = suggestionsIn.filter((sugg) => !isSupportedMod(sugg.modType));
            const phrase = filtered.length === 1 ? 'suggestion was' : 'suggestions were';
            console.log(`Note: ${filtered.length} fragment ${phrase} filtered out because their modType was not recognized.`);
        }
        if (suggestions.length > 0) {
            EventBroker.publish('modifyingCompound', true);
            this.displaySuggestions(suggestions, sourceAtom, cmd, fragList);
        } else {
            const capitalize = (str) => `${str.charAt(0).toUpperCase()}${str.substr(1)}`;
            const title = capitalize(cmd);
            if (errors.length === 0) {
                showAlert(title, 'No candidates found.');
            } else if (!hasPreviewModeError(errors)) {
                showAlert(title, 'Sorry, an error occurred in this search.');
            }
            this.hide();
        }
    }

    /**
     * Change sort after click on column header
     * Cycle when clicking on a column:
     * Sort default direction -> sort opposite direction -> clear sort
     * @param {string} column - The column to sort by
     */
    handleSort(column) {
        this.setState((oldState) => {
            const { primaryScoreField } = oldState;
            const defaultAscending = defaultAscendingForColumn(column);
            if (oldState.sortCol !== column) {
                // changing sort column, use default sort
                return { sortCol: column, sortAscending: defaultAscending };
            } else if (oldState.sortAscending === defaultAscending) {
                // same column, default sort -> switch to opposite direction
                return { sortAscending: !oldState.sortAscending };
            } else {
                // Same column, opposite direction -> unset sort
                return {
                    sortCol: 'default',
                    sortAscending: defaultAscendingForColumn(primaryScoreField),
                };
            }
        });
    }

    handleMultipleFrags() {
        this.setState((oldState) => ({ addMultipleFrags: !oldState.addMultipleFrags }));
    }

    handleHighlightActiveFrags() {
        this.setState((oldState) => ({ highlightActiveFrags: !oldState.highlightActiveFrags }));
    }

    /**
     * Update the activity threshold and remember it for the current primaryScoreField.
     * Recomputes summaryInfo (active/inactive counts) for all suggestions.
     * @param {number} activityThreshold - New threshold value (e.g. -7 for bindingScore)
     */
    handleActivityThreshold(activityThreshold) {
        const { suggestions, primaryScoreField, activityThresholds } = this.state;
        const ati = new ActivityThresholdInfo({
            bestPoseOrderField: primaryScoreField,
            activityField: primaryScoreField,
            activityThreshold,
        });
        for (const sugg of suggestions) {
            sugg.updateSummaryInfo(ati);
        }
        this.setState({
            activityThresholdInfo: ati,
            activityThresholds: { ...activityThresholds, [primaryScoreField]: activityThreshold },
        });
    }

    /**
     * Update the GLOBAL primary-score filter threshold and rederive
     * `displayPoses` for every suggestion. The threshold value is the
     * sole piece of persisted state for this filter — it is remembered
     * per scoring field via `state.filterThresholds`, mirroring the
     * `activityThresholds` pattern so the user's cutoff is preserved
     * when switching `primaryScoreField`.
     *
     * Drag-time behavior is split in two:
     *   1. **Immediate** — `filterThresholds[primaryScoreField]` is updated
     *      synchronously so the slider thumb tracks the pointer and the
     *      readout ("(Showing all)" / number) updates without lag.
     *   2. **Debounced** — the expensive work (rebuild `globalFilter`,
     *      recalc every suggestion's `displayPoses`, redraw poses) is
     *      coalesced into a single trailing call after the user pauses
     *      for `FILTER_COMMIT_DEBOUNCE_MS`. During continuous drag the
     *      table content stays put; on release (or any pause longer than
     *      the debounce) the filter applies in one batch.
     *
     * The actual filter object is built at commit time and passed to
     * `AnnotatedSearchResult.recalculateDisplayPoses` as the separate
     * `globalFilter` argument. It is intentionally NOT installed into
     * `state.allDisplayFilters` — that map is reserved for per-row pose
     * filters managed via `handleUpdateDisplayFilters`. Decoupling the
     * two ensures (a) per-row filters cannot collide with the global
     * filter on the same key and (b) row visibility (driven by
     * `sugg.globalFilteredCount`) is unaffected by per-row filtering.
     *
     * @param {number} filterThreshold
     */
    handleFilterThreshold(filterThreshold) {
        this.setState((oldState) => ({
            filterThresholds: {
                ...oldState.filterThresholds,
                [oldState.primaryScoreField]: filterThreshold,
            },
        }));
        this.commitFilterThresholdDebounced();
    }

    /**
     * Apply the current drafted `filterThresholds[primaryScoreField]` to
     * `state.globalFilter` and recalculate every suggestion's
     * `displayPoses`. Invoked by the trailing edge of
     * `handleFilterThreshold`'s debounce timer.
     */
    commitFilterThreshold() { // eslint-disable-line react/sort-comp
        this.setState((oldState) => {
            const {
                suggestions, suggestionState, sortPosesBy, primaryScoreField,
                allDisplayFilters, filterThresholds,
            } = oldState;
            const globalFilter = buildGlobalScoreFilter(
                filterThresholds[primaryScoreField], primaryScoreField, suggestions
            );
            this.redisplayPoses(suggestions, suggestionState, {
                recalculate: (sugg) => {
                    sugg.recalculateDisplayPoses({
                        sortPosesBy,
                        bestPoseOrderField: primaryScoreField,
                        displayFilters: allDisplayFilters[sugg.name],
                        globalFilter,
                    });
                },
            });
            return { globalFilter };
        });
    }

    handleShowOptions() {
        this.setState((oldState) => ({ showOptions: !oldState.showOptions }));
    }

    toggleColumns(...columns) { // eslint-disable-line react/sort-comp
        this.setState(({ includedColumns: oldIncludedColumns }) => {
            for (const column of columns) {
                if (oldIncludedColumns.has(column)) {
                    oldIncludedColumns.delete(column);
                } else {
                    oldIncludedColumns.add(column);
                }
            }
            return { includedColumns: new Set([...oldIncludedColumns]) };
        });
    }

    /**
     * Change the ordering of poses in the pose explorer slider.
     * Recalculates display poses for all suggestions and re-enters any pinned/active ones.
     * @param {'position'|'bindingScore'|'enSolute'} sortPosesBy
     * @param {{ sugg?: AnnotatedSearchResult }} [options]
     */
    handleSortPosesBy(sortPosesBy, options={}) {
        // Apply pending global filter changes before handling pose explorer filter changes.
        this.commitFilterThresholdDebounced.flush();
        this.setState((oldState) => {
            const {
                suggestions, suggestionState, allDisplayFilters, primaryScoreField,
                globalFilter,
            } = oldState;
            this.redisplayPoses(suggestions, suggestionState, {
                recalculate: (sugg) => {
                    const params = {
                        sortPosesBy,
                        bestPoseOrderField: primaryScoreField,
                        displayFilters: allDisplayFilters[sugg.name],
                        globalFilter,
                    };
                    sugg.recalculateDisplayPoses(params);
                },
                ...options,
            });

            return { sortPosesBy };
        });
    }

    /**
     * Switch the primary scoring field between 'enSolute' and 'bindingScore'.
     *
     * The primary field controls: which column is always visible in the table,
     * which pose is selected as "best" for each row's displayed values, the
     * scoreRatio denominator, and the activity highlight threshold. Switching
     * restores the threshold last used for the new field, or falls back to the
     * field's default (-19 for enSolute, -7 for bindingScore).
     *
     * @param {'enSolute'|'bindingScore'} primaryScoreField
     */
    handlePrimaryScoreField(primaryScoreField, options={}) {
        // Primary score forces a globalFilter rebuild, so cancel any pending globalFilter updates
        this.commitFilterThresholdDebounced.cancel();
        this.setState((oldState) => {
            const {
                suggestions, suggestionState, sortPosesBy, activityThresholds,
                filterThresholds, allDisplayFilters,
            } = oldState;
            const activityThresholdInfo = new ActivityThresholdInfo({
                bestPoseOrderField: primaryScoreField,
                activityField: primaryScoreField,
                activityThreshold: activityThresholds[primaryScoreField] ?? null,
            });
            // Need to rebuild the global filter when primary score changes
            const globalFilter = buildGlobalScoreFilter(
                filterThresholds[primaryScoreField], primaryScoreField, suggestions,
            );
            this.redisplayPoses(suggestions, suggestionState, {
                recalculate: (sugg) => {
                    // When sorting by position, the best pose is always first.
                    // So if changing primaryScoreField with the best pose selected,
                    // we want to select the new best pose for the new field.
                    // This ensures that the selected pose is the one reflected in the
                    // table values. preserveDisplayIndex will do this by choosing new index 0.
                    const preserve = sortPosesBy === 'position' && sugg.selectedDisplayIndex === 0;
                    sugg.primaryScoreField = primaryScoreField;
                    sugg.updateSummaryInfo(activityThresholdInfo);
                    sugg.recalculateBestPoses({ bestPoseOrderField: primaryScoreField });
                    sugg.recalculateDisplayPoses({
                        sortPosesBy,
                        bestPoseOrderField: primaryScoreField,
                        displayFilters: allDisplayFilters[sugg.name],
                        globalFilter,
                        preserveDisplayIndex: preserve,
                    });
                },
                ...options,
            });
            return {
                primaryScoreField,
                activityThresholdInfo,
                globalFilter,
            };
        });
    }

    /**
     * Update pose display filters for one suggestion row and recalculate display poses.
     * Passing null for a filter value removes it; all other values set or replace.
     *
     * Note: the per-pose primary-score filter (keyed on the active
     * `primaryScoreField`) is managed by `handleFilterThreshold` /
     * `handlePrimaryScoreField` and is global across rows; it is not exposed
     * through this column-level filter API.
     *
     * @param {{ [propId: string]: PosePropertyFilter|null }} filters
     * @param {{ sugg?: AnnotatedSearchResult }} [options]
     */
    handleUpdateDisplayFilters(filters, options={}) {
        // Apply pending global filter changes before handling pose explorer filter changes.
        this.commitFilterThresholdDebounced.flush();
        this.setState((oldState) => {
            const {
                suggestions, suggestionState, sortPosesBy, primaryScoreField,
                globalFilter,
            } = oldState;
            const allDisplayFilters = { ...oldState.allDisplayFilters };
            const activeSugg = options.sugg?.name;
            if (!allDisplayFilters[activeSugg]) allDisplayFilters[activeSugg] = {};
            const displayFilters = allDisplayFilters[activeSugg];
            for (const [key, value] of Object.entries(filters)) {
                if (value != null) {
                    displayFilters[key] = value;
                } else {
                    delete displayFilters[key];
                }
            }
            if (Object.keys(displayFilters).length === 0) {
                delete allDisplayFilters[activeSugg];
            }
            this.redisplayPoses(suggestions, suggestionState, {
                recalculate: (sugg) => {
                    sugg.recalculateDisplayPoses({
                        sortPosesBy,
                        bestPoseOrderField: primaryScoreField,
                        displayFilters: allDisplayFilters[sugg.name],
                        globalFilter,
                    });
                },
                ...options,
            });
            return { allDisplayFilters };
        });
    }

    recalculateDisplayPoses(suggestions, params) {
        for (const sugg of suggestions) {
            sugg.recalculateDisplayPoses(params);
        }
    }

    /**
     * Central orchestrator for re-showing fragment previews after a state change.
     * Removes existing temp atoms, optionally recalculates display poses for each suggestion,
     * then re-enters any pinned suggestions plus the actively hovered one.
     * @param {AnnotatedSearchResult[]} suggestions
     * @param {Map<AnnotatedSearchResult, { pinned: boolean }>} oldState - suggestionState map
     * @param {{
     *   recalculate?: function(AnnotatedSearchResult): void,
     *   sugg?: AnnotatedSearchResult,
     * }} [options]
     */
    redisplayPoses(suggestions, oldState, { recalculate, sugg: activeSugg }={}) {
        this.removeTempAtoms(suggestions, { skipRedisplay: true });
        for (const sugg of suggestions) {
            if (recalculate) recalculate(sugg);
            const isPinned = oldState.get(sugg)?.pinned;
            if (sugg === activeSugg || isPinned) {
                this.suggestionEnter(sugg, { force: true, skipRedisplay: true });
            }
            // For pinned non-active suggestions, apply pin behavior (e.g. pinOnlyHighlightedPose).
            if (isPinned && sugg !== activeSugg) {
                this.suggestionExit(sugg, { skipRedisplay: true });
            }
        }
        redisplay();
    }

    /**
     * Handle changes to the "Show Hs" checkbox
     */
    handleFragmentHydrogens() {
        // refreshFrags is invoked in the setTimeout below. The molecule construction needs the
        // updated displayFragmentHydrogens state, so this runs after the state update finishes.
        const refreshFrags = (oldState) => {
            const pinnedSuggestions = oldState.suggestions.filter(
                (sugg) => oldState.suggestionState.get(sugg)?.pinned
            );
            for (const pinnedSugg of pinnedSuggestions) {
                this.suggestionExit(pinnedSugg, { force: true, skipRedisplay: true });
                this.suggestionEnter(pinnedSugg, { force: true, skipRedisplay: true });
            }
            redisplay();
        };

        this.setState((oldState) => {
            setTimeout(() => refreshFrags(oldState), 10); // Refresh after display state update
            return { displayFragmentHydrogens: !oldState.displayFragmentHydrogens };
        });
    }

    /**
     * Handle changes to the "Pin only the highlighted pose" checkbox
     */
    handlePinOnlyHighlightedPose() {
        // refreshFrags runs after the state update so it sees the new pinOnlyHighlightedPose value.
        const refreshFrags = (oldState) => {
            const pinnedSuggestions = oldState.suggestions.filter(
                (sugg) => oldState.suggestionState.get(sugg)?.pinned
            );
            for (const pinnedSugg of pinnedSuggestions) {
                this.suggestionExit(pinnedSugg, { force: true, skipRedisplay: true });
                this.suggestionEnter(pinnedSugg, { force: true, skipRedisplay: true });
                // Apply pin behavior: if pinOnlyHighlightedPose is now on, this
                // removes non-selected poses; if now off, it's a no-op (pinned).
                this.suggestionExit(pinnedSugg, { skipRedisplay: true });
            }
            redisplay();
        };

        this.setState((oldState) => {
            setTimeout(() => refreshFrags(oldState), 10); // Refresh after display state update
            return { pinOnlyHighlightedPose: !oldState.pinOnlyHighlightedPose };
        });
    }

    handleCsvDownload() {
        const csvString = this.getCsvString();
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);

        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.getDownloadName();
        document.body.appendChild(anchor);
        anchor.click();

        URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
    }

    getDownloadName() {
        const { suggestions, uiInfo, sourceAtom } = this.state;
        const isFromDataService = this.fromDataService(suggestions);
        const isGrow = uiInfo.displayLinkType;
        const service = isFromDataService ? 'DataQuery' : 'Search';
        const searchType = isGrow ? 'GrowFrom' : 'Nearby';
        const { mapCase } = App.getDataParents(sourceAtom);
        const caseSuffix = mapCase?.pdbID || mapCase?.case || 'unknown case';
        return `BMaps${service}Results_${caseSuffix}_${searchType}-${sourceAtom.atom}`;
    }

    /**
     * Return the current suggestions sorted for display, along with their column lists.
     * Called from both render() and getCsvString().
     *
     * `suggestions` and `columns` are memoized independently — a sort
     * column click invalidates only the sorted array; column structure
     * stays the same reference. This is what lets `SuggestionRow`'s
     * PureComponent shallow-compare pass for unchanged rows on a sort.
     *
     * @returns {{ suggestions: AnnotatedSearchResult[],
     *             columns: { csvColumns: string[], tableColumns: string[] } }}
     */
    getSortedSuggestions() {
        const {
            suggestions, sortCol, sortAscending, uiInfo,
            showImages, includedColumns, primaryScoreField,
        } = this.state;
        const sortedSuggestions = this.memoSortedSuggestions(
            suggestions, sortCol, sortAscending, primaryScoreField
        );
        const columns = this.memoColumns(
            suggestions, uiInfo, showImages, includedColumns, primaryScoreField
        );
        return { suggestions: sortedSuggestions, columns };
    }

    /**
     * Determine which columns appear in the table and CSV for the current search results.
     *
     * Per-protein columns: primaryScoreField and scoreRatio are promoted to the front.
     * The secondary score field (whichever of enSolute/bindingScore is not primary)
     * is visible in the table only when opted in via includedColumns, but is always
     * present in the CSV. scoreRatio is omitted for the first (reference) protein
     * since that ratio is always 1.0.
     *
     * @param {AnnotatedSearchResult[]} suggestions
     * @param {LigandModUIInfo} uiInfo
     * @param {boolean} showImages
     * @param {Set<string>} includedColumns - user-opted-in optional column keys
     * @param {'enSolute'|'bindingScore'} [primaryScoreField='enSolute']
     * @returns {{ csvColumns: string[], tableColumns: string[] }}
     */
    getColumns(suggestions, uiInfo, showImages, includedColumns, primaryScoreField='enSolute') {
        const {
            perFragColumns, perProteinColumns, linkColumns, summaryColumns,
        } = FragmentSearchResultColumns;
        const projectCases = AnnotatedSearchResult.getProjectCasesForSuggestions(suggestions);
        const displayLink = uiInfo.displayLinkType;
        const displaySummary = projectCases.length > 1;
        const displayCoral = isKinaseInProjectCases(projectCases);

        // Determine per-fragment columns (cols for csv and table are different)
        const fragColsPool = Object.keys(perFragColumns).filter(
            (col) => !perFragColumns[col].skip || includedColumns.has(col)
        );
        const fragColsCsv = fragColsPool.filter((col) => perFragColumns[col].csv);
        const fragColsTable = fragColsPool.filter(
            // name, svg are added to the table separately
            (col) => perFragColumns[col].table && col !== 'name' && col !== 'svg'
        );

        // Determine per-Protein columns
        const secondaryScoreField = primaryScoreField === 'enSolute' ? 'bindingScore' : 'enSolute';
        // Primary score field is always included; secondary is opt-in for table but always in CSV.
        // Primary and ratio are promoted to the front; all others follow in definition order.
        const perProtColsPool = Object.keys(perProteinColumns).filter((col) => {
            if (col === primaryScoreField || col === secondaryScoreField) return true;
            return !perProteinColumns[col].skip || includedColumns.has(col);
        });
        const prioritized = [primaryScoreField, 'scoreRatio'].filter(
            (col) => perProtColsPool.includes(col)
        );
        const orderedPerProtColsPool = [
            ...prioritized,
            ...perProtColsPool.filter((col) => !prioritized.includes(col)),
        ];
        const perProteinColsCsv = [];
        const perProteinColsTable = [];
        for (const pc of projectCases) {
            for (const col of orderedPerProtColsPool) {
                if (pc === projectCases[0] && col === 'scoreRatio') {
                    // Don't need to show the ratio of the primary target to itself (always 1.0)
                    continue;
                }
                if (perProteinColumns[col].csv) {
                    perProteinColsCsv.push(makePerProteinCol(col, pc));
                }
                if (perProteinColumns[col].table) {
                    if (col === secondaryScoreField && !includedColumns.has(col)) continue;
                    perProteinColsTable.push(makePerProteinCol(col, pc));
                }
            }
        }

        // Determine link columns
        const linkColsPool = Object.keys(linkColumns).filter(
            (col) => !linkColumns[col].skip || includedColumns.has(col)
        );
        const linkCols = displayLink ? linkColsPool : [];

        // Determine summary columns (cols for csv and table are different)
        const summColsPool = Object.keys(summaryColumns).filter(
            (col) => !summaryColumns[col].skip || includedColumns.has(col)
        );
        const summCols = displaySummary
            ? summColsPool.filter((col) => (displayCoral || col !== 'coralMap'))
            : [];
        const summColsCsv = summCols.filter((col) => summaryColumns[col].csv);

        // Gather columns together
        const csvColumns = [
            ...fragColsCsv,
            ...linkCols,
            ...perProteinColsCsv,
            ...summColsCsv,
        ];
        const tableColumns = [
            showImages ? 'svg' : 'name',
            ...perProteinColsTable,
            ...fragColsTable,
            ...linkCols,
            ...summCols,
        ];

        return { csvColumns, tableColumns };
    }

    getCsvString() {
        const { suggestions, columns } = this.getSortedSuggestions();
        return getCsvData(suggestions, columns.csvColumns);
    }

    // TODO: remove this
    fromDataService(suggOrSuggs) {
        const sugg = Array.isArray(suggOrSuggs) ? suggOrSuggs[0] : suggOrSuggs;
        return sugg?.origin === 'dataservice';
    }

    // TODO: switch away from tooltipster
    updateTooltips() {
        updateTooltipster({
            '#ligand_mod th': { side: 'top' },
            '.suggestionRow': { side: 'bottom' },
            '.suggestionRow button': { side: 'right' },
        });
    }

    show() {
        this.setState({ visible: true });
    }

    hide() {
        const { suggestions, sourceAtom } = this.state;
        const dataParents = App.getDataParents(sourceAtom);
        this.removeTempAtoms(suggestions);
        for (const sugg of suggestions) {
            App.Workspace.removeFragments(sugg.frags, dataParents);
            // Note: sending dataParents is necessary because these frags are
            // DecodedFragments and not Fragment MolAtomGroups.
            // Unlike MolAtomGroups, DecodedFragments are not able to look up their own
            // data parents.  It would be better to get rid of DecodedFragments and
            // actually build Fragment objects when decoding.
        }

        this.setState({
            visible: false,
            suggestions: [],
            uiInfo: new LigandModUIInfo('grow'),
            suggestionState: new Map(),
        });
        // Turn off bond vectors when closing grow suggestions
        EventBroker.publish('displayBondVectors', { hideAll: true });
        EventBroker.publish('modifyingCompound', false);
    }

    /**
     * Wrap raw search results and show the panel.
     * Converts each {@link FragmentSearchResult} to an {@link AnnotatedSearchResult},
     * assigns display poses using the current primaryScoreField and sortPosesBy,
     * fetches 2D structure images, then sets state to make the panel visible.
     * @param {FragmentSearchResult[]} suggestionsIn - Raw results from bfd-server or dataservice
     * @param {Atom} sourceAtom - Atom the search was launched from
     * @param {string} cmd - 'grow' or 'near'
     * @param {FragList} fragList - Fragment list for fetching 2D images (bfd-server only)
     */
    async displaySuggestions(suggestionsIn, sourceAtom, cmd, fragList) {
        const {
            sortPosesBy, primaryScoreField, allDisplayFilters, activityThresholds,
            filterThresholds,
        } = this.state;

        // Add derived columns
        let hasMultipleFrags = false;
        const suggestionState = new Map();
        const activityThresholdInfo = new ActivityThresholdInfo({
            bestPoseOrderField: primaryScoreField,
            activityField: primaryScoreField,
            activityThreshold: activityThresholds[primaryScoreField] ?? null,
        });

        // Build the global filter once against the cross-suggestion range
        // so the permissive-end check matches what `LigandMod.render` uses
        // for the slider bounds.
        const globalFilter = buildGlobalScoreFilter(
            filterThresholds[primaryScoreField], primaryScoreField, suggestionsIn,
        );

        const suggestions = [];
        for (const [suggIx, suggIn] of suggestionsIn.entries()) {
            const sugg = new AnnotatedSearchResult(suggIn);
            sugg.index = suggIx;
            sugg.primaryScoreField = primaryScoreField;
            sugg.updateSummaryInfo(activityThresholdInfo);
            sugg.recalculateBestPoses({ bestPoseOrderField: primaryScoreField });
            sugg.recalculateDisplayPoses({
                sortPosesBy,
                bestPoseOrderField: primaryScoreField,
                displayFilters: allDisplayFilters[sugg.name],
                globalFilter,
            });

            // In suggestions from bfd-server, the fragList is associated with the target protein.
            // In suggestions from dataservice, there is no fraglist; instead the Workspace method
            // checks the fragLists for all loaded proteins.
            // Is it guaranteed that fragments with the same name will always have the same image?
            // I think so, but just in case, the bfd-server will use the protein's FragList.
            sugg.img = sugg.origin === 'bfd-server'
                ? await fragList.getFragImage(sugg.name) // bfd-server case
                : await App.Workspace.getSvgForFragment(sugg.name); // dataservice case
            if (sugg.frags.length > 1) hasMultipleFrags = true;
            suggestionState.set(sugg, {
                pinned: false,
            });
            suggestions.push(sugg);
        }

        console.log(`Displaying ${suggestions.length} fragment search results.`);

        this.setState({
            suggestions,
            sourceAtom,
            visible: true,
            suggestionState,
            uiInfo: new LigandModUIInfo(cmd, hasMultipleFrags), // labels & widget presence
            globalFilter,
        });
    }

    makeSuggestionMolecule(suggestion, { fragIndex, frag }) {
        const { sourceAtom, displayFragmentHydrogens } = this.state;

        let fragCarbonColor = 0x00FF00;
        const hydrogenDisplay = displayFragmentHydrogens ? HydrogenDisplayOptions.all
            : HydrogenDisplayOptions.polar;
        const atoms = frag.atoms.filter((atom) => hydrogenCheck(atom, hydrogenDisplay));
        const isSelected = fragIndex === suggestion.selectedIndex;
        const opacity = isSelected ? 1 : 0.7;
        if (suggestion.origin === 'dataservice'
            && SelectivityColoring.selectivityActive()
            && suggestion.projectCases.length > 1) {
            fragCarbonColor = SelectivityColoring.getBaseColor(frag.projectCase);
        }

        const Cspec = { C: fragCarbonColor };
        let atomPair = [];
        if (suggestion.modType !== 'Near' && isSelected && suggestion.fragAtomName && sourceAtom) {
            // Draw bond highlight
            const [fragAtom] = frag.atoms.filter((x) => x.atom === suggestion.fragAtomName);
            if (fragAtom) atomPair = [fragAtom, sourceAtom];
        }
        return makeTempMolecule(atoms, MolStyles.sticks, Cspec, opacity, atomPair);
    }

    updateSuggestionMolecule(suggestion, displayPose) {
        const isSelected = displayPose.fragIndex === suggestion.selectedIndex;
        const opacity = isSelected ? 1 : 0.7;
        const style = MolStyles.sticks;
        const model = displayPose.tmpModel;
        if (model) {
            updateTempMolecule(model, { style, opacity });
        }
    }

    /**
     * Show the 3D fragment preview for a suggestion (on hover or pin).
     * Creates a temp molecule for each display pose. No-ops if already pinned, unless forced.
     * @param {AnnotatedSearchResult} suggestion
     * @param {{ force?: boolean, skipRedisplay?: boolean }} [options]
     */
    suggestionEnter(suggestion, { force, skipRedisplay }={}) {
        const { pinOnlyHighlightedPose } = this.state;
        if (!force && this.isPinned(suggestion)) {
            if (pinOnlyHighlightedPose) {
                // Hovering over a pinned-and-collapsed row: restore the full pose cloud.
                for (const displayFrag of suggestion.displayPoses) {
                    if (!displayFrag.frag || displayFrag.tmpModel) continue;
                    displayFrag.tmpModel = this.makeSuggestionMolecule(suggestion, displayFrag);
                }
                if (!skipRedisplay) redisplay();
            }
            return;
        }
        // Not yet pinned (or forced): show all poses.
        for (const displayFrag of suggestion.displayPoses) {
            if (!displayFrag.frag) continue;
            displayFrag.tmpModel = this.makeSuggestionMolecule(suggestion, displayFrag);
        }
        if (!skipRedisplay) {
            redisplay();
        }
    }

    /**
     * Hide the 3D fragment preview for a suggestion (on mouse-out).
     * No-ops if the suggestion is pinned, unless forced.
     * @param {AnnotatedSearchResult} suggestion
     * @param {{ force?: boolean, skipRedisplay?: boolean }} [options]
     */
    suggestionExit(suggestion, { force, skipRedisplay }={}) {
        const { pinOnlyHighlightedPose } = this.state;
        if (!force && this.isPinned(suggestion)) {
            if (pinOnlyHighlightedPose) {
                // Mouse-out of a pinned row: collapse to selected pose only.
                for (const displayPose of suggestion.displayPoses) {
                    if (displayPose.fragIndex !== suggestion.selectedIndex
                            && displayPose.tmpModel) {
                        removeTempMolecule(displayPose.tmpModel);
                        displayPose.tmpModel = null;
                    }
                }
                if (!skipRedisplay) redisplay();
            }
            return;
        }
        this.removeTempAtoms([suggestion], { skipRedisplay });
    }

    /**
     * Confirm the fragment modification for the suggestion's currently selected pose.
     * Removes the preview, triggers the ConfirmModification action, and closes the panel
     * unless addMultipleFrags is on.
     * @param {AnnotatedSearchResult} suggestion
     */
    suggestionAccept(suggestion) {
        this.suggestionExit(suggestion, { force: true });
        const { sourceAtom, addMultipleFrags } = this.state;
        UserActions.ConfirmModification(suggestion.suggestion, sourceAtom, suggestion.selectedFrag);
        if (!addMultipleFrags) {
            this.hide();
        }
    }

    /**
     * Select a different pose in the pose explorer slider.
     * Updates selectedIndex on the suggestion and refreshes the opacity of the old and new
     * temp molecules (selected = full opacity, others = dimmed).
     * @param {AnnotatedSearchResult} suggestion
     * @param {number} displayIndex - Index into suggestion.displayPoses
     */
    cycleSuggestions(suggestion, displayIndex) {
        const newDisplayPose = suggestion.displayPoses[displayIndex];
        const prevFragIndex = suggestion.selectedIndex;
        const prevDisplayPose = suggestion.getDisplayPoseByFragIndex(prevFragIndex);
        suggestion.selectedIndex = newDisplayPose.fragIndex;
        this.updateSuggestionMolecule(suggestion, newDisplayPose);
        this.updateSuggestionMolecule(suggestion, prevDisplayPose);
        redisplay();
    }

    togglePin(sugg) {
        this.setState((oldState) => {
            const suggestionState = oldState.suggestionState.get(sugg);
            suggestionState.pinned = !suggestionState.pinned;
            // Assigning the new Map object to the state field triggers
            // and update, rerendering the necessary children
            return { suggestionState: new Map(oldState.suggestionState) };
        });
    }

    isPinned(sugg) {
        const { suggestionState } = this.state;
        return suggestionState.get(sugg).pinned;
    }

    // Remove temp atoms shown when hovering on frag rows
    removeTempAtoms(suggestions, { skipRedisplay }={}) {
        if (suggestions.length === 0) return;
        for (const suggestion of suggestions) {
            for (const displayPose of suggestion.displayPoses) {
                if (displayPose.tmpModel) {
                    removeTempMolecule(displayPose.tmpModel);
                }
                displayPose.tmpModel = null;
            }
        }
        if (!skipRedisplay) {
            redisplay();
        }
    }

    toggleImages() {
        this.setState((oldState) => ({ showImages: !oldState.showImages }));
    }

    // Display the fragment table table
    render() {
        if (renderDebug) console.log('Rendering LigandMod');
        const {
            visible, sortCol, sortAscending, uiInfo, showImages, sourceAtom,
            addMultipleFrags, displayFragmentHydrogens, highlightActiveFrags, activityThresholdInfo,
            showOptions, includedColumns, sortPosesBy, primaryScoreField, allDisplayFilters,
            pinOnlyHighlightedPose, filterThresholds, suggestions, globalFilter,
        } = this.state;
        if (!visible) return false;

        const { suggestions: sortedSuggestions, columns } = this.getSortedSuggestions();
        // Suppress rows whose every pose has been filtered out by the GLOBAL
        // primary-score slider only. Per-row pose filters can empty
        // `displayPoses` without hiding the row — the per-row pose explorer
        // will surface a "no poses match the specified filter" message
        // instead. `sugg.globalFilteredCount` is set by
        // `AnnotatedSearchResult.recalculateDisplayPoses` whenever the
        // global filter is applied (and defaults to `frags.length` when
        // no global filter is active).
        const visibleSuggestions = this.memoVisibleSuggestions(
            sortedSuggestions, globalFilter
        );
        // Report the number of fragments that were searched for bfd-server searches
        // since not all of the fragments can be loaded at once.
        const showSearchInfo = sortedSuggestions[0]?.origin === 'bfd-server';
        const { caseData } = App.getDataParents(sourceAtom);
        const searchFrags = App.Workspace.getActiveFragments(caseData).length;
        const totalFrags = caseData.getAvailableFragmentInfo().items().length;
        const projectCases = this.memoProjectCases(suggestions);
        const isMultiProtein = projectCases.length > 1;
        const activityInfo = this.memoActivityInfo(highlightActiveFrags, activityThresholdInfo);

        return (
            <div id="ligand_mod" className="canvasPopup">
                <div className="ligand_mod_title">
                    <LinkLikeButton className="ligand_mod_close" style={{ color: 'black' }} onClick={this.hide} aria-label="Close"><i className="fa fa-close" /></LinkLikeButton>
                    <div className="ligand_mod_caption">
                        {globalFilter
                            ? `Showing ${visibleSuggestions.length} / ${sortedSuggestions.length} `
                            : `Found ${visibleSuggestions.length} `}
                        {uiInfo.caption}
                    </div>
                    { showSearchInfo && (
                        <div className="ligand_mod_searchinfo">
                            <span>
                                Searched poses for
                                {' '}
                                {searchFrags}
                                {' '}
                                out of
                                {' '}
                                {totalFrags}
                                {' '}
                                total fragments
                            </span>
                            <LinkLikeButton style={{ color: 'var(--marketing-blue)' }} title="Manage fragment search" onClick={() => UserActions.OpenFragmentManager()}>
                                Manage
                                {' '}
                                searchable
                                {' '}
                                frags
                            </LinkLikeButton>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            padding: '0 1em',
                        }}
                    >
                        <LinkLikeButton
                            style={{ color: 'var(--marketing-blue)', fontSize: 'inherit' }}
                            onClick={this.handleShowOptions}
                        >
                            <i className="fa fa-cog" />
                            {' '}
                            Options
                        </LinkLikeButton>
                        <LinkLikeButton
                            title="Click this button to download the fragment search results in CSV format"
                            style={{
                                color: 'var(--marketing-blue)',
                                fontSize: 'inherit',
                                paddingTop: 0,
                                paddingBottom: 0,
                                textAlign: 'end',
                            }}
                            onClick={this.handleCsvDownload}
                        >
                            <i className="fa fa-download" />
                            {' '}
                            CSV
                        </LinkLikeButton>
                    </div>
                    { !!showOptions && (
                        <div style={{ textAlign: 'start', padding: '0 1em' }}>
                            <div>
                                <label
                                    htmlFor="ligandmod-add-multiple-frags"
                                    title="Check this box to keep the search results open after adding a fragment."
                                >
                                    <input
                                        id="ligandmod-add-multiple-frags"
                                        type="checkbox"
                                        checked={addMultipleFrags}
                                        onChange={this.handleMultipleFrags}
                                    />
                                    {' '}
                                    Add multiple fragments
                                </label>
                            </div>
                            <div>
                                <label
                                    htmlFor="ligandmod-show-hydrogens"
                                    title="Check this box to display all the hydrogens on the fragment suggestions."
                                >
                                    <input
                                        id="ligandmod-show-hydrogens"
                                        type="checkbox"
                                        checked={displayFragmentHydrogens}
                                        onChange={this.handleFragmentHydrogens}
                                    />
                                    {' '}
                                    Show fragment Hs
                                </label>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                <label
                                    htmlFor="ligandmod-highlight-active-results"
                                >
                                    <input
                                        id="ligandmod-highlight-active-results"
                                        type="checkbox"
                                        checked={highlightActiveFrags}
                                        onChange={this.handleHighlightActiveFrags}
                                    />
                                    {' '}
                                    {primaryScoreField === 'enSolute' ? 'Interaction energy' : 'Binding score'}
                                    {' highlight'}
                                </label>
                                {!!highlightActiveFrags && (
                                    <>
                                        <span style={{ padding: '0 0.5em' }}>
                                            (
                                            <span style={{ backgroundColor: ActiveTargetColor }}>
                                                Target
                                            </span>
                                            {' '}
                                            <span style={{ backgroundColor: ActiveOffTargetColor }}>
                                                Off-Target
                                            </span>
                                            )
                                        </span>
                                        Threshold:
                                        <EnergySlider
                                            value={activityThresholdInfo.activityThreshold}
                                            onChange={this.handleActivityThreshold}
                                            min={-20}
                                            max={0}
                                            step={0.5}
                                            style={{ flexGrow: 1 }}
                                            disabled={!highlightActiveFrags}
                                        />
                                        <span>
                                            {activityThresholdInfo.activityThreshold.toFixed(1)}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div>
                                Primary Score:
                                {' '}
                                <label htmlFor="primaryscore-enSolute">
                                    <input
                                        id="primaryscore-enSolute"
                                        type="radio"
                                        name="primaryScoreField"
                                        value="enSolute"
                                        checked={primaryScoreField === 'enSolute'}
                                        onChange={() => this.handlePrimaryScoreField('enSolute')}
                                    />
                                    {' '}
                                    Interaction energy
                                </label>
                                {' '}
                                <label htmlFor="primaryscore-bindingScore">
                                    <input
                                        id="primaryscore-bindingScore"
                                        type="radio"
                                        name="primaryScoreField"
                                        value="bindingScore"
                                        checked={primaryScoreField === 'bindingScore'}
                                        onChange={() => this.handlePrimaryScoreField('bindingScore')}
                                    />
                                    {' '}
                                    Binding score
                                </label>
                            </div>
                            <div>
                                <label
                                    htmlFor="ligandmod-pin-only-highlighted-pose"
                                    title="When pinned, show only the highlighted pose on mouse-out. The full cloud is visible while hovering or using the slider."
                                >
                                    <input
                                        id="ligandmod-pin-only-highlighted-pose"
                                        type="checkbox"
                                        checked={pinOnlyHighlightedPose}
                                        onChange={this.handlePinOnlyHighlightedPose}
                                    />
                                    {' '}
                                    Pin only the highlighted pose
                                </label>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row' }}>
                                Columns:
                                <label
                                    htmlFor="ligandmod-include-ligEff"
                                    style={{ marginLeft: '0.5em' }}
                                >
                                    <input
                                        id="ligandmod-include-ligEff"
                                        type="checkbox"
                                        checked={includedColumns.has('ligandEfficiency')}
                                        onChange={() => this.toggleColumns('ligandEfficiency')}
                                    />
                                    {' '}
                                    Lig. Effcy.
                                </label>
                                {/* Offer option of secondary score */}
                                {primaryScoreField === 'enSolute' ? (
                                    <label
                                        htmlFor="ligandmod-include-bindingScore"
                                        style={{ marginLeft: '0.5em' }}
                                    >
                                        <input
                                            id="ligandmod-include-bindingScore"
                                            type="checkbox"
                                            checked={includedColumns.has('bindingScore')}
                                            onChange={() => this.toggleColumns('bindingScore')}
                                        />
                                        {' '}
                                        Binding score
                                    </label>
                                ) : (
                                    <label
                                        htmlFor="ligandmod-include-enSolute"
                                        style={{ marginLeft: '0.5em' }}
                                    >
                                        <input
                                            id="ligandmod-include-enSolute"
                                            type="checkbox"
                                            checked={includedColumns.has('enSolute')}
                                            onChange={() => this.toggleColumns('enSolute')}
                                        />
                                        {' '}
                                        Interaction energy
                                    </label>
                                )}
                                <label
                                    htmlFor="ligandmod-include-enSolv"
                                    style={{ marginLeft: '0.5em' }}
                                >
                                    <input
                                        id="ligandmod-include-enSolv"
                                        type="checkbox"
                                        checked={includedColumns.has('enSolv')}
                                        onChange={() => this.toggleColumns('enSolv')}
                                    />
                                    {' '}
                                    &Delta;&Delta;G
                                    <sub>s</sub>
                                </label>
                                <label
                                    htmlFor="ligandmod-include-exchemP"
                                    style={{ marginLeft: '0.5em' }}
                                >
                                    <input
                                        id="ligandmod-include-exchemP"
                                        type="checkbox"
                                        checked={includedColumns.has('exchemPotential')}
                                        onChange={() => this.toggleColumns('exchemPotential')}
                                    />
                                    {' '}
                                    Ex. Chem. Potential
                                </label>
                                { isMultiProtein && (
                                    <label
                                        htmlFor="ligandmod-include-targetCounts"
                                        style={{ marginLeft: '0.5em' }}
                                    >
                                        <input
                                            id="ligandmod-include-targetCounts"
                                            type="checkbox"
                                            checked={includedColumns.has('activeTargetCount')}
                                            onChange={() => this.toggleColumns(
                                                'activeTargetCount',
                                                'activeOffTargetCount',
                                                'selectivityRatio'
                                            )}
                                        />
                                        {' '}
                                        Target counts
                                    </label>
                                )}
                                { uiInfo.displayLinkType && (
                                    <label
                                        htmlFor="ligandmod-include-fragAtom"
                                        style={{ marginLeft: '0.5em' }}
                                    >
                                        <input
                                            id="ligandmod-include-fragAtom"
                                            type="checkbox"
                                            checked={includedColumns.has('fragAtomName')}
                                            onChange={() => this.toggleColumns('fragAtomName')}
                                        />
                                        {' '}
                                        Frag. attachment atom
                                    </label>
                                )}
                                <label
                                    htmlFor="ligandmod-include-posebuckets"
                                    style={{ marginLeft: '0.5em' }}
                                    title="In the CSV download, include columns with the number of poses in each binding score bucket"
                                >
                                    {' '}
                                    <input
                                        id="ligandmod-include-posebuckets"
                                        type="checkbox"
                                        checked={includedColumns.has('poseBucket0_1')}
                                        onChange={() => this.toggleColumns(
                                            'poseBucket0_1',
                                            'poseBucket1_2',
                                            'poseBucket2_3',
                                            'poseBucket3_4',
                                            'poseBucket4_5',
                                            'poseBucket5_6',
                                            'poseBucket6_7',
                                            'poseBucket7_8',
                                            'poseBucket8_9',
                                            'poseBucket9_10',
                                            'poseBucket10_11',
                                            'poseBucket11_12',
                                            'poseBucket12_13',
                                            'poseBucket13_14',
                                            'poseBucket14_15',
                                            'poseBucket15_16',
                                            'poseBucket16_17',
                                            'poseBucket17_18',
                                            'poseBucket18_19',
                                            'poseBucket19_20',
                                            'poseBucket20_X',
                                            'poseBucketX_0',
                                        )}
                                    />
                                    {' '}
                                    CSV pose buckets
                                </label>
                                <label
                                    htmlFor="ligandmod-include-enSoluteAvg"
                                    style={{ marginLeft: '0.5em' }}
                                >
                                    <input
                                        id="ligandmod-include-enSoluteAvg"
                                        type="checkbox"
                                        checked={includedColumns.has('enSoluteAvg')}
                                        onChange={() => this.toggleColumns('enSoluteAvg')}
                                    />
                                    {' '}
                                    Avg inter. energy
                                </label>
                            </div>
                        </div>
                    )}
                </div>
                <GlobalFilterSlider
                    suggestions={suggestions}
                    primaryScoreField={primaryScoreField}
                    value={filterThresholds[primaryScoreField]}
                    onChange={this.handleFilterThreshold}
                />
                <div className="scroller" ref={this.scrollingRef}>
                    { sortedSuggestions.length > 0 ? (
                        <SuggestionTable
                            sortedSuggestions={visibleSuggestions}
                            columns={columns.tableColumns}
                            uiInfo={uiInfo}
                            sortCol={sortCol}
                            sortAscending={sortAscending}
                            handleSort={this.handleSort}
                            showImages={showImages}
                            toggleImages={this.toggleImages}
                            suggestionAccept={this.suggestionAccept}
                            suggestionEnter={this.suggestionEnter}
                            suggestionExit={this.suggestionExit}
                            cycleSuggestions={this.cycleSuggestions}
                            isPinned={this.isPinned}
                            togglePin={this.togglePin}
                            activityInfo={activityInfo}
                            projectCases={projectCases}
                            sortPosesBy={sortPosesBy}
                            handleSortPosesBy={this.handleSortPosesBy}
                            allDisplayFilters={allDisplayFilters}
                            handleUpdateDisplayFilters={this.handleUpdateDisplayFilters}
                            globalFilter={globalFilter}
                        />
                    ) : (
                        <table>
                            <tbody>
                                <tr key="no-suggestions-row" className="suggestionRow"><td colSpan="4">Sorry, no fragments found.</td></tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }
}
